import React, { useMemo, useState } from "react";
import {
  ChevronRight, ChevronDown, Plus, Pencil, Trash2, ArrowUp, ArrowDown,
  User, CalendarDays, Clock,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { TASK_STATUSES, PRIORITIES, statusMeta, buildTaskTree, flattenTree } from "@/lib/taskman";

function Badge({ meta, testid }) {
  const cls = "inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wide " + meta.cls;
  return <span data-testid={testid} className={cls}>{meta.label}</span>;
}

function IconBtn({ onClick, title, testid, danger, children }) {
  const cls = danger
    ? "h-6 w-6 grid place-items-center rounded-sm text-zinc-400 hover:bg-red-50 hover:text-red-600"
    : "h-6 w-6 grid place-items-center rounded-sm text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700";
  return (
    <button type="button" onClick={onClick} title={title} className={cls} data-testid={testid}>
      {children}
    </button>
  );
}

function RowMeta({ task }) {
  const showDates = task.start_date || task.due_date;
  const showHours = task.estimate_hours != null || task.actual_hours != null;
  return (
    <>
      {task.assignee?.name && (
        <span className="hidden md:inline-flex items-center gap-1 text-xs text-zinc-500 shrink-0">
          <User className="h-3 w-3" />{task.assignee.name}
        </span>
      )}
      {showDates && (
        <span className="hidden lg:inline-flex items-center gap-1 text-xs text-zinc-500 shrink-0">
          <CalendarDays className="h-3 w-3" />
          {task.start_date || "…"} → {task.due_date || "…"}
        </span>
      )}
      {showHours && (
        <span className="hidden xl:inline-flex items-center gap-1 text-xs text-zinc-500 shrink-0">
          <Clock className="h-3 w-3" />
          {task.actual_hours ?? 0}/{task.estimate_hours ?? "?"}h
        </span>
      )}
    </>
  );
}

export default function TaskTree({
  tasks, rootSequential = false,
  onEdit, onAddSub, onDelete, onQuickStatus, onReorder,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const byParent = useMemo(() => buildTaskTree(tasks), [tasks]);
  const rows = useMemo(() => flattenTree(tasks), [tasks]);
  const byId = useMemo(() => new Map(tasks.map((t) => [t.task_id, t])), [tasks]);

  const isHidden = (t) => {
    let pid = t.parent_task_id;
    while (pid) {
      if (collapsed.has(pid)) return true;
      pid = byId.get(pid)?.parent_task_id;
    }
    return false;
  };

  const toggle = (taskId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const move = (task, dir) => {
    const siblings = byParent.get(task.parent_task_id || "__root__") || [];
    const ids = siblings.map((s) => s.task_id);
    const i = ids.indexOf(task.task_id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    onReorder(ids);
  };

  if (!rows.length) {
    return (
      <div className="text-sm text-zinc-400 py-8 text-center" data-testid="task-tree-empty">
        No tasks yet.
      </div>
    );
  }

  return (
    <div data-testid="task-tree">
      {rows.map((task) => {
        if (isHidden(task)) return null;
        const children = byParent.get(task.task_id) || [];
        const parent = task.parent_task_id ? byId.get(task.parent_task_id) : null;
        const containerSequential = parent
          ? parent.execution_mode === "sequential" : rootSequential;
        const siblings = byParent.get(task.parent_task_id || "__root__") || [];
        const index = siblings.findIndex((s) => s.task_id === task.task_id);
        const pr = statusMeta(PRIORITIES, task.priority);
        const st = statusMeta(TASK_STATUSES, task.status);
        const isOpen = !collapsed.has(task.task_id);
        const struck = task.status === "done" || task.status === "cancelled";
        const titleCls = "text-sm text-left truncate flex-1 min-w-0 hover:underline " +
          (struck ? "line-through text-zinc-400" : "text-zinc-800");
        const caretCls = "h-4 w-4 grid place-items-center text-zinc-400 " +
          (children.length ? "" : "invisible");

        return (
          <div key={task.task_id}
            className="group flex items-center gap-2 py-1.5 px-2 rounded-sm hover:bg-zinc-50 border-b border-zinc-100"
            style={{ paddingLeft: 8 + task.depth * 24 }}
            data-testid={`task-row-${task.task_id}`}>
            <button type="button" className={caretCls} onClick={() => toggle(task.task_id)}
              data-testid={`task-toggle-${task.task_id}`}>
              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            {containerSequential && (
              <span className="h-5 w-5 shrink-0 grid place-items-center rounded-full bg-zinc-900 text-white text-[10px] font-bold"
                title="Runs in priority order">{index + 1}</span>
            )}
            <button type="button" onClick={() => onEdit(task)} className={titleCls}
              data-testid={`task-title-${task.task_id}`}>
              {task.title}
              {children.length > 0 && (
                <span className="ml-2 text-[10px] text-zinc-400">
                  {children.length} subtask{children.length > 1 ? "s" : ""}
                  {task.execution_mode === "sequential" ? " · ordered" : ""}
                </span>
              )}
            </button>
            <RowMeta task={task} />
            <Badge meta={pr} testid={`task-priority-${task.task_id}`} />
            <Select value={task.status} onValueChange={(v) => onQuickStatus(task, v)}>
              <SelectTrigger className="h-6 w-auto gap-1 border-0 p-0 shadow-none focus:ring-0"
                data-testid={`task-quick-status-${task.task_id}`}>
                <Badge meta={st} />
              </SelectTrigger>
              <SelectContent>
                {TASK_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <IconBtn onClick={() => move(task, -1)} title="Move up" testid={`task-up-${task.task_id}`}>
                <ArrowUp className="h-3.5 w-3.5" />
              </IconBtn>
              <IconBtn onClick={() => move(task, 1)} title="Move down" testid={`task-down-${task.task_id}`}>
                <ArrowDown className="h-3.5 w-3.5" />
              </IconBtn>
              <IconBtn onClick={() => onAddSub(task)} title="Add subtask" testid={`task-add-sub-${task.task_id}`}>
                <Plus className="h-3.5 w-3.5" />
              </IconBtn>
              <IconBtn onClick={() => onEdit(task)} title="Edit" testid={`task-edit-${task.task_id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </IconBtn>
              <IconBtn onClick={() => onDelete(task)} title="Delete (incl. subtasks)" danger
                testid={`task-delete-${task.task_id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </IconBtn>
            </div>
          </div>
        );
      })}
    </div>
  );
}
